import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, Send, X, Mic, Sparkles, Loader2, 
  Trash2, Calendar, Copy, ExternalLink, User, Bot,
  CheckCircle, Users, Search, DoorOpen, Phone,
  AlertTriangle, ArrowRight, CreditCard, Clock, BedDouble, Grid3X3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { useBrowserSTT } from '@/hooks/useBrowserSTT';
import { useBrowserTTS } from '@/hooks/useBrowserTTS';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChatAnalyticsWidget } from '@/components/ai/ChatAnalyticsWidget';

interface PriceCalculation {
  category_id?: string;
  category_name: string;
  total_price: number;
  check_in: string;
  check_out: string;
  nights: number;
  adults?: number;
  children_count?: number;
  average_per_night?: number;
  can_book?: boolean;
  error?: string;
}

interface MiceCalculation {
  participants: number;
  days: number;
  venue_name: string;
  venue_type?: string;
  venues_cost: number;
  catering_cost: number;
  catering_breakdown?: Array<{ item: string; cost: number; count: number }>;
  accommodation_cost: number;
  accommodation_rooms?: number;
  total_cost: number;
  per_person: number;
}

interface CreatedBooking {
  id: string;
  reference: string;
  room_number: string;
  category_name?: string;
  check_in: string;
  check_out: string;
  nights?: number;
  total: number;
  guest_name?: string;
}

interface SearchResult {
  guests?: Array<{ id: string; first_name: string; last_name: string; phone?: string }>;
  available_rooms?: Array<{ id: string; room_number: string; room_type: string }>;
  bookings?: Array<{ id: string; booking_reference: string; check_in_date: string; check_out_date: string; status: string; guest_name?: string }>;
}

interface PendingConfirmation {
  operation: string;
  booking_id: string;
  booking_reference: string;
  guest_name: string;
  room_number?: string;
  room_type?: string;
  check_in?: string;
  check_out?: string;
  current: Record<string, unknown>;
  proposed: Record<string, unknown>;
  summary: string;
  amount?: number;
  payment_method?: string;
  extra_nights?: number;
  price_difference?: number;
  target_room_id?: string;
  target_room_number?: string;
  service_name?: string;
  service_price?: number;
  status?: 'pending' | 'confirmed' | 'cancelled' | 'error';
  reason?: string;
}

interface AnalyticsWidgetData {
  type: 'overview' | 'revenue' | 'occupancy' | 'kpi' | 'bookings';
  dateFrom?: string;
  dateTo?: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: Array<{ action: string; params: Record<string, unknown> }>;
  priceCalculation?: PriceCalculation;
  miceCalculation?: MiceCalculation;
  createdBooking?: CreatedBooking;
  searchResults?: SearchResult;
  pendingConfirmation?: PendingConfirmation;
  analyticsWidget?: AnalyticsWidgetData;
}

// Page mapping for navigate action
const PAGE_ROUTES: Record<string, string> = {
  'dashboard': '/admin',
  'bookings': '/admin/bookings',
  'room-grid': '/admin/room-grid',
  'guests': '/admin/guests',
  'analytics': '/admin/analytics',
  'finance': '/admin/finance',
  'settings': '/admin/settings',
  'ai-assistant': '/admin/ai-assistant',
  'rooms': '/admin/rooms',
  'calendar': '/admin/calendar',
};

export const AIAssistantChat: React.FC = () => {
  const { toast } = useToast();
  const { currentHotel } = useOptimizedHotel();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingBookingData, setPendingBookingData] = useState<PriceCalculation | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Widget enabled state
  const [widgetEnabled, setWidgetEnabled] = useState(() => {
    const saved = localStorage.getItem('voice_widget_enabled');
    return saved !== 'false';
  });

  // Voice settings with autoSpeak enabled by default
  const [settings] = useState(() => {
    const saved = localStorage.getItem('voice_settings');
    const defaultSettings = { 
      enabled: true, 
      mode: 'browser_tts', 
      language: 'ru-RU',
      autoSpeak: true,
      speechRate: 1.0
    };
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return { ...defaultSettings, ...parsed };
      } catch {
        return defaultSettings;
      }
    }
    return defaultSettings;
  });

  // Browser TTS
  const browserTTS = useBrowserTTS({
    language: settings.language || 'ru-RU',
    rate: settings.speechRate || 1.0,
  });

  // Browser STT
  const browserSTT = useBrowserSTT({
    language: settings.language || 'ru-RU',
    onResult: (text, isFinal) => {
      setInputValue(text);
      if (isFinal && text.trim()) {
        handleSubmit(text.trim());
      }
    },
    onError: (error) => {
      toast({
        title: "Ошибка распознавания",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Scroll to bottom when new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Listen for settings changes
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('voice_widget_enabled');
      setWidgetEnabled(saved !== 'false');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0 && currentHotel) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: `Привет! Я AI-ассистент отеля "${currentHotel.name}". Я могу:\n\n• 📋 Открыть любую бронь: "Открой бронь Ивановой"\n• 💰 Рассчитать стоимость и создать бронь\n• 🔍 Найти гостей: "Найди Петрова"\n• 🏛️ MICE-расчёт: вставьте текст запроса\n• 🧭 Навигация: "Открой шахматку / финансы"`,
        timestamp: new Date(),
      }]);
    }
  }, [isOpen, messages.length, currentHotel]);

  // Format date for display
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  };

  // ===== ACTION EXECUTOR =====
  const executeActions = useCallback((actions: Array<{ action: string; params: Record<string, unknown> }>, results: Record<string, unknown>, messageId?: string) => {
    for (const { action, params } of actions) {
      // Navigate
      if (action === 'navigate') {
        const page = params.page as string;
        const route = PAGE_ROUTES[page];
        if (route) {
          setTimeout(() => navigate(route), 300);
        }
      }

      // Open booking modal
      if (action === 'open_booking') {
        const bookingId = params.booking_id as string;
        if (bookingId) {
          window.dispatchEvent(new CustomEvent('open-booking-modal', { detail: { bookingId } }));
        }
      }

      // Show booking on grid (шахматка)
      if (action === 'show_on_grid') {
        const bookingId = params.booking_id as string;
        const checkInDate = params.check_in_date as string;
        if (bookingId) {
          queryClient.clear();
          navigate(`/admin/room-grid?bookingId=${bookingId}&date=${checkInDate}`);
        }
      }

      // Create booking draft -> open quick booking
      if (action === 'create_booking_draft') {
        window.dispatchEvent(new CustomEvent('open-quick-booking', {
          detail: {
            guestName: params.guest_name as string,
            guestPhone: params.phone as string,
            checkIn: params.check_in as string,
            checkOut: params.check_out as string,
            category: params.room_type as string,
            adults: (params.guests_count as number) || 1,
          }
        }));
      }

      // WhatsApp
      if (action === 'send_quote_whatsapp') {
        const whatsappUrl = (results?.whatsapp_quote as any)?.whatsapp_url;
        if (whatsappUrl) {
          window.open(whatsappUrl, '_blank');
        }
      }

      // 🔒 Pending confirmation — store in message, don't execute
      if (action === 'pending_confirmation' && messageId) {
        const confirmation: PendingConfirmation = {
          operation: params.operation as string,
          booking_id: params.booking_id as string,
          booking_reference: params.booking_reference as string || '',
          guest_name: params.guest_name as string || '',
          room_number: params.room_number as string,
          room_type: params.room_type as string,
          check_in: params.check_in as string,
          check_out: params.check_out as string,
          current: params.current as Record<string, unknown> || {},
          proposed: params.proposed as Record<string, unknown> || {},
          summary: params.summary as string || '',
          amount: params.amount as number,
          payment_method: params.payment_method as string,
          extra_nights: params.extra_nights as number,
          price_difference: params.price_difference as number,
          target_room_id: params.target_room_id as string,
          target_room_number: params.target_room_number as string,
          service_name: params.service_name as string,
          service_price: params.service_price as number,
          status: 'pending',
        };

        setMessages(prev => prev.map(m => 
          m.id === messageId ? { ...m, pendingConfirmation: confirmation } : m
        ));
      }
    }
  }, [navigate]);

  // Handle confirm booking from price calculation card
  const handleConfirmBooking = useCallback(async (priceCalc: PriceCalculation) => {
    setPendingBookingData(priceCalc);
    
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: `Забронируй ${priceCalc.category_name} с ${formatDate(priceCalc.check_in)} по ${formatDate(priceCalc.check_out)}`,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    
    handleSubmit(`Забронируй ${priceCalc.category_name} с ${priceCalc.check_in} по ${priceCalc.check_out}, ${priceCalc.adults || 2} взрослых, сумма ${priceCalc.total_price}`);
  }, []);

  // Copy quote to clipboard
  const handleCopyQuote = useCallback((priceCalc: PriceCalculation) => {
    const text = `🏨 ${priceCalc.category_name}
📅 ${formatDate(priceCalc.check_in)} — ${formatDate(priceCalc.check_out)} (${priceCalc.nights} ночей)
💰 ${priceCalc.total_price.toLocaleString('ru-RU')} ₽
${priceCalc.average_per_night ? `(${priceCalc.average_per_night.toLocaleString('ru-RU')} ₽/ночь)` : ''}`;
    
    navigator.clipboard.writeText(text);
    toast({ title: "Скопировано!", description: "Расчёт скопирован в буфер обмена" });
  }, [toast]);

  // Copy MICE proposal
  const handleCopyMiceProposal = useCallback((mice: MiceCalculation) => {
    let text = `MICE РАСЧЁТ
👥 ${mice.participants} участников × ${mice.days} ${mice.days === 1 ? 'день' : 'дня'}

🏛️ ${mice.venue_name}: ${mice.venues_cost.toLocaleString('ru-RU')} ₽`;
    
    if (mice.catering_cost > 0) {
      text += `\n🍽️ Кейтеринг: ${mice.catering_cost.toLocaleString('ru-RU')} ₽`;
    }
    if (mice.accommodation_cost > 0) {
      text += `\n🛏️ Проживание (${mice.accommodation_rooms} номеров): ${mice.accommodation_cost.toLocaleString('ru-RU')} ₽`;
    }
    
    text += `\n\n═══════════════════════════
ИТОГО: ${mice.total_cost.toLocaleString('ru-RU')} ₽
(${mice.per_person.toLocaleString('ru-RU')} ₽ на человека)`;

    navigator.clipboard.writeText(text);
    toast({ title: "Скопировано!", description: "MICE-расчёт скопирован" });
  }, [toast]);

  // Open booking in modal
  const handleOpenBooking = useCallback((bookingId: string) => {
    window.dispatchEvent(new CustomEvent('open-booking-modal', { detail: { bookingId } }));
  }, []);

  // 🔒 HANDLE CONFIRM ACTION — execute dangerous operation after user approval
  const handleConfirmAction = useCallback(async (messageId: string, confirmation: PendingConfirmation) => {
    // Update card to show loading
    setMessages(prev => prev.map(m => 
      m.id === messageId ? { ...m, pendingConfirmation: { ...confirmation, status: 'confirmed' } } : m
    ));

    try {
      const { operation, booking_id } = confirmation;

      if (operation === 'change_status') {
        const newStatus = confirmation.proposed.status as string;
        const reason = confirmation.reason || `AI-ассистент: ${confirmation.summary}`;
        
        await supabase.rpc('save_booking_atomic', {
          p_booking_id: booking_id,
          p_hotel_id: currentHotel?.id || '',
          p_changes: { status: newStatus, ...(newStatus === 'cancelled' ? { cancellation_reason: reason, cancelled_at: new Date().toISOString() } : {}) } as any,
          p_change_reason: reason,
        });
      }

      if (operation === 'add_payment') {
        await supabase.from('booking_payments').insert({
          booking_id,
          hotel_id: currentHotel?.id || '',
          amount: confirmation.amount!,
          payment_method: confirmation.payment_method || 'cash',
          payment_type: 'payment',
          payment_date: new Date().toISOString().split('T')[0],
          currency: 'RUB',
          status: 'completed',
          notes: `AI-ассистент: ${confirmation.summary}`,
        });
        await supabase.rpc('recalc_booking_paid_amount', { p_booking_id: booking_id });
      }

      if (operation === 'extend_booking') {
        const newCheckOut = confirmation.proposed.check_out as string;
        const newTotal = confirmation.proposed.total as number;
        const reason = `AI-ассистент: продление на ${confirmation.extra_nights} ноч.`;
        
        await supabase.rpc('save_booking_atomic', {
          p_booking_id: booking_id,
          p_hotel_id: currentHotel?.id || '',
          p_changes: { check_out_date: newCheckOut } as any,
          p_change_reason: reason,
        });
      }

      if (operation === 'move_booking') {
        const reason = confirmation.reason || `AI-ассистент: переселение ${confirmation.room_number} → ${confirmation.target_room_number}`;
        // Need booking data for move_booking_atomic
        const { data: bk } = await supabase.from('booking_core').select('check_in_date, check_out_date, version').eq('id', booking_id).single();
        
        await supabase.rpc('move_booking_atomic', {
          p_booking_id: booking_id,
          p_new_room_id: confirmation.target_room_id!,
          p_new_check_in: bk?.check_in_date || '',
          p_new_check_out: bk?.check_out_date || '',
          p_expected_version: bk?.version || 1,
          p_change_reason: reason,
        });
      }

      if (operation === 'add_service') {
        await supabase.from('booking_services').insert({
          booking_id,
          service_name: confirmation.service_name!,
          service_type: 'additional',
          unit_price: confirmation.service_price || 0,
          quantity: 1,
          total_price: confirmation.service_price || 0,
          status: 'confirmed',
        });
        // Серверный пересчёт total_amount через RPC (как SuperAdvancedModal)
        await supabase.rpc('save_booking_atomic', {
          p_booking_id: booking_id,
          p_hotel_id: currentHotel?.id || '',
          p_changes: {} as any,
          p_change_reason: `AI-ассистент: добавлена услуга "${confirmation.service_name}"`,
        });
      }

      // Success — update card
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, pendingConfirmation: { ...confirmation, status: 'confirmed' } } : m
      ));
      toast({ title: "Выполнено!", description: confirmation.summary });

      // AK-47: Полная инвалидация кэша + broadcast для других вкладок
      await queryClient.invalidateQueries({ queryKey: ['optimized-bookings'] });
      await queryClient.invalidateQueries({ queryKey: ['bookings'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      window.dispatchEvent(new CustomEvent('clear-selectel-cache'));

      // Broadcast для синхронизации других терминалов
      try {
        const channel = supabase.channel('booking-changes-bot');
        await channel.send({
          type: 'broadcast',
          event: 'booking_change',
          payload: { type: 'UPDATE', booking_id, hotel_id: currentHotel?.id }
        });
        supabase.removeChannel(channel);
      } catch (e) {
        console.warn('Broadcast error (non-critical):', e);
      }

    } catch (err: any) {
      console.error('❌ Confirm action error:', err);
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, pendingConfirmation: { ...confirmation, status: 'error' } } : m
      ));
      toast({ title: "Ошибка", description: err.message || 'Не удалось выполнить операцию', variant: "destructive" });
    }
  }, [currentHotel?.id, toast]);

  // Cancel pending confirmation
  const handleCancelConfirmation = useCallback((messageId: string, confirmation: PendingConfirmation) => {
    setMessages(prev => prev.map(m => 
      m.id === messageId ? { ...m, pendingConfirmation: { ...confirmation, status: 'cancelled' } } : m
    ));
  }, []);

  // Send WhatsApp voucher
  const handleSendVoucher = useCallback((booking: CreatedBooking) => {
    const text = `✅ Подтверждение бронирования

📋 Номер брони: ${booking.reference}
🏨 Категория: ${booking.category_name || 'Номер'}
🚪 Комната: ${booking.room_number}
📅 Заезд: ${formatDate(booking.check_in)}
📅 Выезд: ${formatDate(booking.check_out)}
💰 Сумма: ${booking.total.toLocaleString('ru-RU')} ₽

Ждём вас в нашем отеле!`;

    navigator.clipboard.writeText(text);
    toast({ title: "Скопировано!", description: "Ваучер скопирован для отправки" });
  }, [toast]);

  // Handle message submission
  const handleSubmit = useCallback(async (text?: string) => {
    const command = text || inputValue.trim();
    if (!command || isProcessing) return;

    if (!text) {
      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: command,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMessage]);
    }
    
    setInputValue('');
    setIsProcessing(true);

    try {
      const { data, error } = await supabase.functions.invoke('voice-command', {
        body: { 
          command, 
          hotel_id: currentHotel?.id,
          chat_history: messages.slice(-10).map(m => ({
            role: m.role,
            content: m.content
          }))
        }
      });

      if (error) throw error;

      // Build search results from response
      const searchResults: SearchResult = {};
      if (data.results?.guests?.length) searchResults.guests = data.results.guests;
      if (data.results?.available_rooms?.length) searchResults.available_rooms = data.results.available_rooms;
      if (data.results?.bookings?.length) searchResults.bookings = data.results.bookings;

      // Build analytics widget data from stats/finance results
      let analyticsWidget: AnalyticsWidgetData | undefined;
      const today = new Date().toISOString().split('T')[0];
      if (data.results?.stats) {
        const statsDate = data.results.stats.date || today;
        analyticsWidget = { type: 'overview', dateFrom: statsDate, dateTo: statsDate };
      } else if (data.results?.finance) {
        analyticsWidget = { 
          type: 'revenue', 
          dateFrom: data.results.finance.period_start || today,
          dateTo: data.results.finance.period_end || today 
        };
      }

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.speak || 'Готово!',
        timestamp: new Date(),
        actions: data.actions,
        priceCalculation: data.results?.price_calculation,
        miceCalculation: data.results?.mice_calculation,
        createdBooking: data.results?.created_booking,
        searchResults: Object.keys(searchResults).length > 0 ? searchResults : undefined,
        analyticsWidget,
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Clear pending booking data if booking was created
      if (data.results?.created_booking) {
        setPendingBookingData(null);
      }

      // 🚀 EXECUTE ACTIONS automatically
      if (data.actions?.length > 0) {
        executeActions(data.actions, data.results || {}, assistantMessage.id);
      }

      // TTS for response
      if (settings.autoSpeak && data.speak && browserTTS.isSupported) {
        browserTTS.speak(data.speak);
      }

    } catch (err) {
      console.error('AI Assistant error:', err);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Произошла ошибка. Попробуйте ещё раз.',
        timestamp: new Date(),
      }]);
    } finally {
      setIsProcessing(false);
    }
  }, [inputValue, isProcessing, currentHotel?.id, messages, settings.autoSpeak, browserTTS, executeActions]);

  // Toggle microphone
  const toggleMic = useCallback(() => {
    if (browserSTT.isListening) {
      browserSTT.stop();
    } else {
      setInputValue('');
      browserSTT.start();
    }
  }, [browserSTT]);

  // Clear chat history
  const clearChat = useCallback(() => {
    setMessages([]);
    setPendingBookingData(null);
    toast({ title: "История очищена" });
  }, [toast]);

  // Render clickable search results
  const renderSearchResults = (results: SearchResult) => {
    return (
      <div className="mt-2 space-y-1.5">
        {/* Guest results */}
        {results.guests?.map((guest) => (
          <Card key={guest.id} className="p-2 bg-accent/30 hover:bg-accent/50 transition-colors cursor-pointer">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <User className="h-3.5 w-3.5 text-primary shrink-0" />
                <span className="text-sm font-medium truncate">
                  {guest.last_name} {guest.first_name}
                </span>
                {guest.phone && (
                  <span className="text-xs text-muted-foreground truncate">
                    {guest.phone}
                  </span>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-xs shrink-0"
                onClick={() => handleSubmit(`Покажи брони гостя ${guest.last_name} ${guest.first_name}`)}
              >
                <Search className="h-3 w-3 mr-1" /> Брони
              </Button>
            </div>
          </Card>
        ))}

        {/* Booking results */}
        {results.bookings?.map((booking) => (
          <Card key={booking.id} className="p-2 bg-accent/30 hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => handleOpenBooking(booking.id)}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Calendar className="h-3.5 w-3.5 text-primary shrink-0" />
                <span className="text-sm font-medium">{booking.booking_reference}</span>
                <Badge variant="outline" className="text-[10px] h-4">{booking.status}</Badge>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs shrink-0">
                  <DoorOpen className="h-3 w-3 mr-1" /> Открыть
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    queryClient.clear();
                    navigate(`/admin/room-grid?bookingId=${booking.id}&date=${booking.check_in_date}`);
                  }}
                >
                  <Grid3X3 className="h-3 w-3 mr-1" /> Шахматка
                </Button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground mt-0.5 ml-5">
              {formatDate(booking.check_in_date)} — {formatDate(booking.check_out_date)}
              {booking.guest_name && ` • ${booking.guest_name}`}
            </div>
          </Card>
        ))}

        {/* Available rooms */}
        {results.available_rooms?.map((room) => (
          <Card key={room.id} className="p-2 bg-accent/30 hover:bg-accent/50 transition-colors">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <DoorOpen className="h-3.5 w-3.5 text-primary" />
                <span className="text-sm font-medium">№{room.room_number}</span>
                <Badge variant="secondary" className="text-[10px] h-4">{room.room_type}</Badge>
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  if (!widgetEnabled) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <AnimatePresence mode="wait">
        {isOpen ? (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="bg-background border shadow-2xl rounded-2xl flex flex-col w-[90vw] max-w-[420px] h-[70vh] max-h-[600px]"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-primary-foreground" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">AI Ассистент</span>
                  <span className="text-xs text-muted-foreground truncate max-w-[180px]">
                    {currentHotel?.name || 'Отель'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={clearChat}
                    title="Очистить историю"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setIsOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-3">
              <div className="space-y-3">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      "flex gap-2",
                      message.role === 'user' ? "justify-end" : "justify-start"
                    )}
                  >
                    {message.role === 'assistant' && (
                      <div className="h-6 w-6 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center">
                        <Bot className="h-3 w-3 text-primary" />
                      </div>
                    )}
                    <div
                      className={cn(
                        "rounded-2xl px-3 py-2 max-w-[85%] text-sm",
                        message.role === 'user'
                          ? "bg-primary text-primary-foreground rounded-br-md"
                          : "bg-muted rounded-bl-md"
                      )}
                    >
                      <p className="whitespace-pre-wrap break-words">{message.content}</p>
                      
                      {/* Analytics widget */}
                      {message.analyticsWidget && (
                        <ChatAnalyticsWidget 
                          type={message.analyticsWidget.type}
                          dateFrom={message.analyticsWidget.dateFrom}
                          dateTo={message.analyticsWidget.dateTo}
                          onNavigate={() => setIsOpen(false)}
                        />
                      )}
                      
                      {/* Clickable search results */}
                      {message.searchResults && renderSearchResults(message.searchResults)}

                      {/* Price calculation card with booking button */}
                      {message.priceCalculation && !message.priceCalculation.error && (
                        <Card className="mt-2 p-3 bg-gradient-to-r from-accent/50 to-accent/30 border-accent">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium">{message.priceCalculation.category_name}</span>
                            <Badge variant="secondary" className="text-xs">
                              {message.priceCalculation.nights} ноч.
                            </Badge>
                          </div>
                          <div className="text-lg font-bold text-primary">
                            {message.priceCalculation.total_price.toLocaleString('ru-RU')} ₽
                          </div>
                          {message.priceCalculation.average_per_night && (
                            <div className="text-xs text-muted-foreground">
                              {message.priceCalculation.average_per_night.toLocaleString('ru-RU')} ₽/ночь
                            </div>
                          )}
                          
                          {/* Action buttons */}
                          <div className="flex gap-2 mt-3">
                            {message.priceCalculation.can_book && (
                              <Button 
                                size="sm" 
                                onClick={() => handleConfirmBooking(message.priceCalculation!)}
                                className="flex-1"
                              >
                                <Calendar className="h-3 w-3 mr-1" /> Забронировать
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleCopyQuote(message.priceCalculation!)}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </Card>
                      )}

                      {/* MICE calculation card */}
                      {message.miceCalculation && (
                        <Card className="mt-2 p-3 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30 border-purple-200 dark:border-purple-800">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                            <span className="font-medium text-sm">MICE расчёт</span>
                            <Badge variant="secondary" className="text-xs">
                              {message.miceCalculation.participants} чел × {message.miceCalculation.days} дн.
                            </Badge>
                          </div>
                          
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">🏛️ {message.miceCalculation.venue_name}</span>
                              <span>{message.miceCalculation.venues_cost.toLocaleString('ru-RU')} ₽</span>
                            </div>
                            {message.miceCalculation.catering_cost > 0 && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">🍽️ Кейтеринг</span>
                                <span>{message.miceCalculation.catering_cost.toLocaleString('ru-RU')} ₽</span>
                              </div>
                            )}
                            {message.miceCalculation.accommodation_cost > 0 && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">🛏️ Проживание</span>
                                <span>{message.miceCalculation.accommodation_cost.toLocaleString('ru-RU')} ₽</span>
                              </div>
                            )}
                          </div>
                          
                          <Separator className="my-2" />
                          
                          <div className="flex justify-between font-bold">
                            <span>Итого:</span>
                            <span className="text-purple-700 dark:text-purple-400">
                              {message.miceCalculation.total_cost.toLocaleString('ru-RU')} ₽
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground text-center mt-1">
                            {message.miceCalculation.per_person.toLocaleString('ru-RU')} ₽ на человека
                          </div>
                          
                          <div className="flex gap-2 mt-3">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleCopyMiceProposal(message.miceCalculation!)}
                              className="flex-1"
                            >
                              <Copy className="h-3 w-3 mr-1" /> Скопировать КП
                            </Button>
                          </div>
                        </Card>
                      )}

                      {/* Created booking card */}
                      {message.createdBooking && (
                        <Card className="mt-2 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200 dark:border-green-800">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                            <span className="font-medium text-sm text-green-800 dark:text-green-300">
                              Бронь создана
                            </span>
                          </div>
                          
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Номер:</span>
                              <span className="font-medium">{message.createdBooking.reference}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Комната:</span>
                              <span>{message.createdBooking.room_number}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Даты:</span>
                              <span>
                                {formatDate(message.createdBooking.check_in)} — {formatDate(message.createdBooking.check_out)}
                              </span>
                            </div>
                          </div>
                          
                          <Separator className="my-2" />
                          
                          <div className="flex justify-between font-bold">
                            <span>Итого:</span>
                            <span className="text-green-700 dark:text-green-400">
                              {message.createdBooking.total.toLocaleString('ru-RU')} ₽
                            </span>
                          </div>
                          
                          <div className="flex gap-2 mt-3">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleOpenBooking(message.createdBooking!.id)}
                              className="flex-1"
                            >
                              <ExternalLink className="h-3 w-3 mr-1" /> Открыть
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => handleSendVoucher(message.createdBooking!)}
                            >
                              <MessageSquare className="h-3 w-3 mr-1" /> Ваучер
                            </Button>
                          </div>
                        </Card>
                      )}

                      {/* 🔒 Confirmation card for dangerous operations */}
                      {message.pendingConfirmation && (
                        <Card className={cn(
                          "mt-2 p-3 border-2",
                          message.pendingConfirmation.status === 'confirmed' 
                            ? "border-green-500 bg-green-50 dark:bg-green-950/30" 
                            : message.pendingConfirmation.status === 'cancelled'
                            ? "border-muted bg-muted/30 opacity-60"
                            : message.pendingConfirmation.status === 'error'
                            ? "border-destructive bg-destructive/10"
                            : "border-amber-500 bg-amber-50 dark:bg-amber-950/20"
                        )}>
                          {/* Header: always booking_reference + guest_name */}
                          <div className="flex items-center gap-2 mb-2">
                            {message.pendingConfirmation.status === 'confirmed' ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : message.pendingConfirmation.status === 'error' ? (
                              <AlertTriangle className="h-4 w-4 text-destructive" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 text-amber-600" />
                            )}
                            <span className="font-bold text-sm">
                              {message.pendingConfirmation.booking_reference} | {message.pendingConfirmation.guest_name}
                            </span>
                          </div>

                          {/* Operation label */}
                          <Badge variant="outline" className="mb-2 text-xs">
                            {message.pendingConfirmation.operation === 'change_status' && (
                              <>{message.pendingConfirmation.proposed.status === 'checked_in' ? '🚪 Заселение' : message.pendingConfirmation.proposed.status === 'checked_out' ? '🚶 Выселение' : '❌ Аннулирование'}</>
                            )}
                            {message.pendingConfirmation.operation === 'add_payment' && <>💰 Оплата</>}
                            {message.pendingConfirmation.operation === 'extend_booking' && <>📅 Продление</>}
                            {message.pendingConfirmation.operation === 'move_booking' && <>🔄 Переселение</>}
                            {message.pendingConfirmation.operation === 'add_service' && <>✨ Услуга</>}
                          </Badge>

                          {/* Current → Proposed */}
                          <div className="space-y-1 text-xs">
                            {message.pendingConfirmation.operation === 'change_status' && (
                              <>
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground">Было:</span>
                                  <Badge variant="secondary">{String(message.pendingConfirmation.current.status)}</Badge>
                                  <ArrowRight className="h-3 w-3" />
                                  <span className="text-muted-foreground">Станет:</span>
                                  <Badge>{String(message.pendingConfirmation.proposed.status)}</Badge>
                                </div>
                                {message.pendingConfirmation.room_number && (
                                  <div className="text-muted-foreground">Комната: {message.pendingConfirmation.room_number}</div>
                                )}
                              </>
                            )}

                            {message.pendingConfirmation.operation === 'add_payment' && (
                              <>
                                <div className="flex items-center gap-2">
                                  <CreditCard className="h-3 w-3" />
                                  <span className="font-medium">{(message.pendingConfirmation.amount || 0).toLocaleString('ru-RU')} ₽</span>
                                  <Badge variant="secondary">{
                                    {cash: 'Наличные', card: 'Карта', transfer: 'Перевод', online: 'Онлайн'}[message.pendingConfirmation.payment_method || 'cash'] || message.pendingConfirmation.payment_method
                                  }</Badge>
                                </div>
                                <div className="text-muted-foreground">
                                  Баланс: {Number(message.pendingConfirmation.current.balance || 0).toLocaleString('ru-RU')} → {Number(message.pendingConfirmation.proposed.balance || 0).toLocaleString('ru-RU')} ₽
                                </div>
                              </>
                            )}

                            {message.pendingConfirmation.operation === 'extend_booking' && (
                              <>
                                <div className="flex items-center gap-2">
                                  <Clock className="h-3 w-3" />
                                  <span>+{message.pendingConfirmation.extra_nights} ноч.</span>
                                </div>
                                <div className="text-muted-foreground">
                                  Выезд: {formatDate(String(message.pendingConfirmation.current.check_out))} → {formatDate(String(message.pendingConfirmation.proposed.check_out))}
                                </div>
                                {(message.pendingConfirmation.price_difference || 0) > 0 && (
                                  <div className="font-medium">Доплата: {(message.pendingConfirmation.price_difference || 0).toLocaleString('ru-RU')} ₽</div>
                                )}
                              </>
                            )}

                            {message.pendingConfirmation.operation === 'move_booking' && (
                              <div className="flex items-center gap-2">
                                <BedDouble className="h-3 w-3" />
                                <span>{message.pendingConfirmation.room_number}</span>
                                <ArrowRight className="h-3 w-3" />
                                <span className="font-medium">{message.pendingConfirmation.target_room_number}</span>
                              </div>
                            )}

                            {message.pendingConfirmation.operation === 'add_service' && (
                              <div className="flex items-center gap-2">
                                <span>{message.pendingConfirmation.service_name}</span>
                                {(message.pendingConfirmation.service_price || 0) > 0 && (
                                  <span className="font-medium">{(message.pendingConfirmation.service_price || 0).toLocaleString('ru-RU')} ₽</span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Status messages */}
                          {message.pendingConfirmation.status === 'confirmed' && (
                            <div className="mt-2 text-xs font-medium text-green-700 dark:text-green-400">✅ Выполнено</div>
                          )}
                          {message.pendingConfirmation.status === 'cancelled' && (
                            <div className="mt-2 text-xs text-muted-foreground">Отменено</div>
                          )}
                          {message.pendingConfirmation.status === 'error' && (
                            <div className="mt-2 text-xs text-destructive">Ошибка выполнения</div>
                          )}

                          {/* Action buttons — only for pending */}
                          {message.pendingConfirmation.status === 'pending' && (
                            <div className="flex gap-2 mt-3">
                              <Button 
                                size="sm" 
                                onClick={() => handleConfirmAction(message.id, message.pendingConfirmation!)}
                                className="flex-1"
                              >
                                <CheckCircle className="h-3 w-3 mr-1" /> Подтвердить
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleCancelConfirmation(message.id, message.pendingConfirmation!)}
                              >
                                <X className="h-3 w-3 mr-1" /> Отмена
                              </Button>
                            </div>
                          )}
                          {/* View booking button — always visible */}
                          {message.pendingConfirmation.booking_id && (
                            <Button
                              size="sm"
                              variant="link"
                              className="mt-1 p-0 h-auto text-xs text-primary"
                              onClick={() => handleOpenBooking(message.pendingConfirmation!.booking_id)}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" /> Смотреть бронь
                            </Button>
                          )}
                        </Card>
                      )}
                    </div>
                    {message.role === 'user' && (
                      <div className="h-6 w-6 rounded-full bg-primary flex-shrink-0 flex items-center justify-center">
                        <User className="h-3 w-3 text-primary-foreground" />
                      </div>
                    )}
                  </div>
                ))}
                
                {isProcessing && (
                  <div className="flex gap-2 justify-start">
                    <div className="h-6 w-6 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center">
                      <Bot className="h-3 w-3 text-primary" />
                    </div>
                    <div className="bg-muted rounded-2xl rounded-bl-md px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-muted-foreground">Думаю...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input area */}
            <div className="p-3 border-t">
              <div className="flex items-center gap-2">
                {/* Mic button */}
                <Button
                  variant={browserSTT.isListening ? "destructive" : "outline"}
                  size="icon"
                  onClick={toggleMic}
                  disabled={!browserSTT.isSupported}
                  className={cn(
                    "shrink-0 h-9 w-9",
                    browserSTT.isListening && "animate-pulse"
                  )}
                >
                  <Mic className="h-4 w-4" />
                </Button>

                {/* Text input */}
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={browserSTT.isListening ? "Слушаю..." : "Спросите что-нибудь..."}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSubmit()}
                  className="flex-1 h-9"
                />

                {/* Send button */}
                <Button
                  variant="default"
                  size="icon"
                  onClick={() => handleSubmit()}
                  disabled={!inputValue.trim() || isProcessing}
                  className="shrink-0 h-9 w-9"
                >
                  {isProcessing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
              
              {browserSTT.isListening && (
                <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="w-2 h-2 bg-destructive rounded-full animate-pulse" />
                  Запись...
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
          >
            <Button
              size="lg"
              onClick={() => setIsOpen(true)}
              className="rounded-full h-14 w-14 shadow-xl bg-gradient-to-br from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
            >
              <MessageSquare className="h-6 w-6" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
